import { useState, useMemo } from 'react'
import { API } from 'aws-amplify'
import { Paper, SvgIcon, IconButton, CircularProgress, Dialog, DialogContent, DialogActions, DialogTitle, DialogContentText, Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGridPremium, GridColDef, useGridApiRef, GridRowModesModel, GridRowModes, GridRowParams, GridRowModel, GridEventListener, GridRowEditStopReasons, GridCallbackDetails } from '@mui/x-data-grid-premium';
import { Status, useFetch } from '../../hooks/useFetch';
import { AirlineFields, ExcludedLegFields } from '@aviation/catering-masterdata-sdk';
import { DynamoDbObject } from '@aviation/catering-common';
import StyledBox from '../../common/Components/StyledBox'
import { ReactComponent as TrashIcon } from '../../icons/trash.svg';
import { ReactComponent as CrossIcon } from '../../icons/cross.svg';
import { ReactComponent as CheckmarkIcon } from '../../icons/checkmark.svg';
import { toast } from "react-toastify";
import { ulid } from 'ulidx';
import ExcludedLegCreateDialog from './ExcludedLegCreateDialog';

export interface IExcludedLegsGridProps {
    clientCode: string;
    showCreateDialog: boolean;
    cancelAction: () => void;
}

function ExcludedLegsGrid(props: IExcludedLegsGridProps) {
    const apiRef = useGridApiRef();
    const { status, data = [] } = useFetch<Array<ExcludedLegFields & DynamoDbObject>>(`/api/masterdata/excludedlegs/${props.clientCode}`);

    /* eslint-disable @typescript-eslint/no-unused-vars */
    const { data: airlineData = [] } = useFetch<Array<AirlineFields & DynamoDbObject>>(`/api/masterdata/airline/${props.clientCode}`);

    const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [deleteItem, setDeleteItem] = useState<ExcludedLegFields & DynamoDbObject | undefined>(undefined);
    const [loading, setLoading] = useState(false);

    const rowId = (row: any): string => {
        return row.Id;
    }

    const rows = useMemo(() => {
        return data.map(o => {
            return {
                ...o,
                id: rowId(o)
            }
        })
    }, [data]);

    const airlineList = useMemo(() => {
        return airlineData.map(o => { return { label: (o as any).Description ?? '', value: o.RK } }).sort((a, b) => a.label < b.label ? -1 : 1);
    }, [airlineData])

    const columns: GridColDef[] = [
        { field: 'Airline', headerName: 'Airline', minWidth: 150, type: 'singleSelect', valueOptions: airlineList },
        { field: 'Dep', headerName: 'Departure', minWidth: 100, editable: false },
        { field: 'Dest', headerName: 'Destination', minWidth: 100, editable: false },
        {
            field: 'actions', type: 'actions', headerName: '', minWidth: 55, renderCell: ({ row }: Partial<GridRowParams>) => {
                const isInEditMode = (rowModesModel[rowId(row)]?.mode ?? GridRowModes.View) === GridRowModes.Edit;
                const isLoading = row.IsLoading ?? false;

                if (isInEditMode)
                    return [
                        <IconButton color="success" onClick={handleSaveClick(row)}>
                            <SvgIcon component={CheckmarkIcon} inheritViewBox />
                        </IconButton>,
                        <IconButton color="error" onClick={handleCancelClick(rowId(row))}>
                            <SvgIcon component={CrossIcon} inheritViewBox />
                        </IconButton>
                    ]
                else if (!isLoading)
                    return (
                        <IconButton color="error" onClick={() => confirmDeletion(row)}>
                            <SvgIcon component={TrashIcon} inheritViewBox />
                        </IconButton>
                    )
                else if (isLoading)
                    return (
                        <CircularProgress size={19} />
                    )
            }
        }
    ];

    const handleRowModesModelChange = (model: GridRowModesModel, details: GridCallbackDetails) => {
        setRowModesModel(model);
    };

    const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut)
            event.defaultMuiPrevented = true;
        else
            setRowModesModel({ ...rowModesModel, [params.id]: { mode: GridRowModes.View } });

    };

    const processRowUpdate = (row: GridRowModel) => {
        const updatedRow = { ...row, isNew: false, IsLoading: true };

        updateItem(row as ExcludedLegFields & DynamoDbObject).then(success => {
            apiRef.current.updateRows([{ id: rowId(row), IsLoading: false }]);

            if (success)
                toast.success(`Changes to item saved successfully.`);
            else
                toast.error(`An error occurred while saving item`);
        })
        return updatedRow;
    };

    const handleSaveClick = (item: any) => () => {
        setRowModesModel({ ...rowModesModel, [rowId(item) ?? '']: { mode: GridRowModes.View } });
    };

    const handleCancelClick = (id: any) => () => {
        setRowModesModel({
            ...rowModesModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        });
    };

    const handleCloseDialog = () => {
        setDeleteItem(undefined);
        setShowDeleteConfirmation(false);
    };

    const handleDeleteItem = async () => {
        setShowDeleteConfirmation(false);

        if (deleteItem !== undefined) {
            apiRef.current.updateRows([{ id: rowId(deleteItem), IsLoading: true }]);

            const init = {
                body: {},
                headers: {}
            };

            try {
                const id = rowId(deleteItem);
                await API.del('api', `/api/masterdata/excludedlegs/${props.clientCode}/${id}`, init);
                apiRef.current.updateRows([{ id: rowId(deleteItem), _action: 'delete' }]);
                toast.success(`Item deleted successfully.`);
                return true;
            } catch (e) {
                console.error(e);
                apiRef.current.updateRows([{ id: rowId(deleteItem), IsLoading: false }]);
                toast.error(`An error occurred while deleting an item`);
                return false;
            } finally {
                setDeleteItem(undefined);
            }
        }
    };

    const updateItem = async (item: ExcludedLegFields) => {
        const init = {
            body: {...item},
            headers: {}
        };

        delete (init.body as any).id;

        const id = rowId(item);

        try {
            await API.put('api', `/api/masterdata/excludedlegs/${props.clientCode}/${id}`, init);
            return true;
        } catch (e) {
            console.error(e);
            return false;
        }
    }

    const confirmDeletion = (item: any) => {
        setDeleteItem(item);
        setShowDeleteConfirmation(true);
    };

    const onCreateItem = async (airline: string, dep: string, dest: string) => {
        props.cancelAction();
        setLoading(true);

        var id = ulid().toString();
        const item: ExcludedLegFields = {
            Id: id,
            Dep: dep,
            Dest: dest,
            Airline: airline
        };

        // Define id and airline for grid usage
        (item as any).id = rowId(item);

        if (await updateItem(item)) {
            setLoading(false);
            apiRef.current.updateRows([item]);
            toast.success(`Excluded leg added successfully`);
        } else {
            setLoading(false);
            toast.error(`An error occurred while creating excluded leg`);
        }
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: '100px' }} elevation={3}>
                <StyledBox>
                    <DataGridPremium
                        apiRef={apiRef}
                        autoHeight
                        rows={rows}
                        columns={columns}
                        editMode="row"
                        getRowClassName={(params) => { return params.indexRelativeToCurrentPage % 2 === 1 ? `tui-grid-alternate-row` : '' }}
                        pagination
                        loading={status === Status.Fetching || status === Status.Idle || loading}
                        isCellEditable={(params) => true}

                        rowModesModel={rowModesModel}
                        onRowModesModelChange={(m, d) => handleRowModesModelChange(m, d)}
                        onRowEditStop={handleRowEditStop}
                        processRowUpdate={processRowUpdate}
                    />
                </StyledBox>
                {airlineList && (<ExcludedLegCreateDialog airlineList={airlineList} cancelAction={() => props.cancelAction()} createAction={onCreateItem} open={props.showCreateDialog} />)}
                <Dialog open={showDeleteConfirmation} onClose={handleCloseDialog}>
                    <DialogTitle id="alert-dialog-title">
                        {"Delete Item?"}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            Do you want to delete this excluded leg? Pax meal figures will then appear for those. You can't undo this action.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog} autoFocus>Cancel</Button>
                        <Button onClick={handleDeleteItem} color="error">Delete</Button>
                    </DialogActions>
                </Dialog>
            </Paper>
        </Grid>
    )
}

export default ExcludedLegsGrid;